// Image Editor - Main component that composes all sub-components
import React from 'react';
import { Box, VStack, Grid, GridItem, Input } from '@chakra-ui/react';
import { useImageEditor } from './hooks/useImageEditor';
import {
  UploadScreen,
  EditorHeader,
  ImageTabs,
  PreviewSection,
  ProcessedResult,
  DimensionsControl,
  QualityControl,
  FormatSelector,
  FileSizeDisplay,
  ActionButtons,
} from './components';

const ImageEditor: React.FC = () => {
  const editor = useImageEditor();

  // Show upload screen if no images
  if (editor.images.length === 0) {
    return (
      <UploadScreen
        fileInputRef={editor.fileInputRef}
        isDragging={editor.isDragging}
        setIsDragging={editor.setIsDragging}
        handleDrop={editor.handleDrop}
        handleInputChange={editor.handleInputChange}
      />
    );
  }

  // Editor Screen
  return (
    <Box minH="100vh" bg="background.primary" py={4} px={4}>
      <VStack maxW="1400px" mx="auto" spacing={4} align="stretch">
        {/* Header */}
        <EditorHeader onReset={editor.resetEditor} />

        {/* Tab Navigation */}
        <ImageTabs
          images={editor.images}
          currentIndex={editor.currentIndex}
          onTabClick={editor.setCurrentIndex}
          onRemove={editor.removeImage}
          onAddMore={() => editor.fileInputRef.current?.click()}
        />

        {/* Main Content - Two Column Layout */}
        <Grid templateColumns={{ base: '1fr', lg: '1fr 320px' }} gap={4}>
          {/* Left Column - Preview */}
          <GridItem>
            <VStack spacing={4} align="stretch">
              <PreviewSection
                currentImage={editor.currentImage}
                imageRef={editor.imageRef}
                isCropping={editor.isCropping}
                crop={editor.crop}
                setCrop={editor.setCrop}
                setCompletedCrop={editor.setCompletedCrop}
                completedCrop={editor.completedCrop}
                cropAspect={editor.cropAspect}
                isApplyingCrop={editor.isApplyingCrop}
                isResetting={editor.isResetting}
                hasBeenCropped={editor.hasBeenCropped}
                onStartCropping={editor.startCropping}
                onCancelCrop={editor.cancelCrop}
                onApplyCrop={editor.applyCrop}
                onSetCropAspectRatio={editor.setCropAspectRatio}
                onResetCrop={editor.resetCrop}
              />

              <ProcessedResult
                currentImage={editor.currentImage}
                onDownload={editor.downloadImage}
              />
            </VStack>
          </GridItem>

          {/* Right Column - Controls */}
          <GridItem>
            <VStack spacing={4} align="stretch">
              <DimensionsControl
                width={editor.width}
                height={editor.height}
                aspectLocked={editor.aspectLocked}
                currentImage={editor.currentImage}
                onWidthChange={editor.handleWidthChange}
                onHeightChange={editor.handleHeightChange}
                onToggleAspectLock={editor.toggleAspectLock}
              />

              <QualityControl quality={editor.quality} onQualityChange={editor.setQuality} />

              <FormatSelector
                outputFormat={editor.outputFormat}
                onFormatChange={editor.setOutputFormat}
              />

              <FileSizeDisplay
                currentImage={editor.currentImage}
                savedBytes={editor.savedBytes}
                savedPercent={editor.savedPercent}
                formatFileSize={editor.formatFileSize}
              />

              <ActionButtons
                currentImage={editor.currentImage}
                isProcessing={editor.isProcessing}
                isCropping={editor.isCropping}
                needsProcessing={editor.needsProcessing}
                onProcess={editor.processImage}
                onDownload={editor.downloadImage}
              />
            </VStack>
          </GridItem>
        </Grid>

        {/* Hidden File Input */}
        <Input
          ref={editor.fileInputRef}
          type="file"
          accept="image/*"
          multiple
          hidden
          onChange={editor.handleInputChange}
        />
      </VStack>
    </Box>
  );
};

export default ImageEditor;
